/**
 * FinanceEntityDrawer — slide-in detail panel for a single FinancialEntity.
 *
 * Tabs:
 *   - Snapshots: form to record monthly P&L + 3 sparklines + history table + CSV import
 *   - Thresholds: 5 per-entity warning overrides
 *   - Forecast: 12-month projected-cash chart based on burn trend
 *
 * Also surfaces the linked legal entity (entities_v2) when present, with a
 * direct link out to /dashboard/entities-os/{id} so the fiscal and legal
 * views are one click apart.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  X, Plus, Upload, Trash2, Save, ExternalLink, TrendingUp, BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import Sparkline from '@/components/Sparkline';
import { STATUS_META, SEVERITY_META, fmtCurrency, fmtMonths } from '@/components/finance/financeUtils';

const TABS = [
  { key: 'snapshots',  label: 'Snapshots' },
  { key: 'forecast',   label: 'Forecast' },
  { key: 'thresholds', label: 'Thresholds' },
];

export default function FinanceEntityDrawer({ entityId, onClose, onChanged, isAdmin }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('snapshots');
  const [form, setForm] = useState({
    period: '', cash_position: '', monthly_revenue: '', monthly_expenses: '',
    headcount: '', new_funding: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [thresholds, setThresholds] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/finance/entities/${entityId}`);
      setData(d);
      setThresholds(d.entity?.thresholds || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load entity');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [entityId, onClose]);

  /* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch effect */
  useEffect(() => { reload(); }, [reload]);
  /* eslint-enable */

  const saveSnapshot = async () => {
    if (!form.period.match(/^\d{4}-(0[1-9]|1[0-2])$/)) {
      return toast.error('Period must be YYYY-MM (e.g. 2026-02)');
    }
    if (form.cash_position === '') return toast.error('Cash position is required');
    setSaving(true);
    try {
      const payload = {
        period: form.period,
        cash_position: parseFloat(form.cash_position),
        monthly_revenue: parseFloat(form.monthly_revenue || 0),
        monthly_expenses: parseFloat(form.monthly_expenses || 0),
        new_funding: parseFloat(form.new_funding || 0),
        headcount: form.headcount ? parseInt(form.headcount) : null,
        notes: form.notes || null,
      };
      await api.post(`/finance/entities/${entityId}/snapshots`, payload);
      toast.success(`Snapshot for ${form.period} recorded`);
      setForm({ period: '', cash_position: '', monthly_revenue: '', monthly_expenses: '', headcount: '', new_funding: '', notes: '' });
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record snapshot');
    } finally {
      setSaving(false);
    }
  };

  const deleteSnapshot = async (snapId) => {
    if (!window.confirm('Delete this snapshot?')) return;
    try {
      await api.delete(`/finance/entities/${entityId}/snapshots/${snapId}`);
      toast.success('Snapshot deleted');
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const importCSV = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data: r } = await api.post(
        `/finance/entities/${entityId}/snapshots/import`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      toast.success(`Imported ${r.created} new, updated ${r.updated}${r.errors.length ? `, ${r.errors.length} errors` : ''}`);
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    }
  };

  const saveThresholds = async () => {
    try {
      await api.patch(`/finance/entities/${entityId}`, { thresholds });
      toast.success('Thresholds updated');
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const deleteEntity = async () => {
    if (!window.confirm(`Delete entity "${data?.entity?.name}" and all its snapshots? This cannot be undone.`)) return;
    try {
      await api.delete(`/finance/entities/${entityId}`);
      toast.success('Entity deleted');
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  // 12-month cash-position forecast derived from snapshot history.
  // Server may include `forecast` in the runway payload (preferred — uses
  // the same model the email reports rely on); if absent, we compute a
  // simple straight-line projection client-side so the panel never breaks.
  const forecast = useMemo(() => {
    if (data?.runway?.forecast?.length) return data.runway.forecast;
    if (!data) return [];
    const rw = data.runway || {};
    if (rw.cash_position == null || !rw.avg_monthly_burn_3mo) return [];
    const out = [];
    let cash = rw.cash_position;
    for (let i = 1; i <= 12; i++) {
      cash = cash - rw.avg_monthly_burn_3mo;
      out.push({
        month_offset: i,
        label: `M+${i}`,
        projected_cash: Math.round(cash),
        status: cash < 0 ? 'red' : (cash < rw.avg_monthly_burn_3mo * 6 ? 'amber' : 'green'),
      });
    }
    return out;
  }, [data]);

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center" onClick={onClose}>
        <div className="text-white">Loading…</div>
      </div>
    );
  }
  if (!data) return null;

  const rw = data.runway;
  const meta = STATUS_META[rw.runway_status] || STATUS_META.unknown;
  const legal = data.legal_entity;  // populated server-side when entity.legal_entity_id is set

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="absolute right-0 top-0 bottom-0 w-full max-w-2xl bg-white shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="finance-entity-drawer"
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 p-5 flex items-start justify-between z-10">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Entity detail</div>
            <div className="text-2xl font-semibold text-slate-900">{data.entity.name}</div>
            <div className="text-xs text-slate-500 mt-1">
              {data.entity.sector || 'Uncategorised'} · {data.entity.currency} · {data.entity.status}
            </div>
            {legal && (
              <RouterLink
                to={`/dashboard/entities-live/${legal.entity_id}`}
                data-testid="finance-legal-entity-link"
                className="inline-flex items-center gap-1 mt-2 text-xs text-[#5E1F8B] hover:text-[#8a6c10] hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="w-3 h-3" />
                Linked legal record: <strong>{legal.legal_name || legal.name}</strong>
                {legal.cr_number ? <span className="text-slate-500">· CR {legal.cr_number}</span> : null}
              </RouterLink>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="finance-drawer-close" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Runway KPIs */}
        <div className="p-5">
          <div className="grid grid-cols-4 gap-3 mb-5">
            <div className="bg-slate-50 rounded-xl p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Cash</div>
              <div className="text-lg font-semibold text-slate-900 tabular-nums">{fmtCurrency(rw.cash_position, data.entity.currency)}</div>
            </div>
            <div className="rounded-xl p-3" style={{ backgroundColor: meta.bg }}>
              <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: meta.text }}>Runway</div>
              <div className="text-lg font-semibold tabular-nums" style={{ color: meta.accent }}>
                {fmtMonths(rw.runway_months)}
              </div>
              <div className="text-[10px] mt-1" style={{ color: meta.text }}>{meta.label}</div>
            </div>
            <div className="bg-slate-50 rounded-xl p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Burn 3mo</div>
              <div className="text-lg font-semibold text-slate-900 tabular-nums">{fmtCurrency(rw.avg_monthly_burn_3mo, data.entity.currency)}</div>
            </div>
            <div className="bg-slate-50 rounded-xl p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Burn 6mo</div>
              <div className="text-lg font-semibold text-slate-900 tabular-nums">{fmtCurrency(rw.avg_monthly_burn_6mo, data.entity.currency)}</div>
            </div>
          </div>

          {/* Warnings inline */}
          {data.warnings && data.warnings.length > 0 && (
            <div className="mb-5 space-y-2" data-testid="finance-drawer-warnings">
              {data.warnings.map((w, i) => {
                const wm = SEVERITY_META[w.severity];
                const Icon = wm.icon;
                return (
                  <div key={i} className="rounded-xl border p-3 flex items-start gap-2"
                    style={{ backgroundColor: wm.bg, borderColor: wm.accent + '40' }}>
                    <Icon className="w-4 h-4 mt-0.5" style={{ color: wm.accent }} />
                    <div className="text-sm" style={{ color: wm.text }}>{w.message}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Tabs */}
          <div className="flex border-b border-slate-200 mb-4">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`finance-tab-${t.key}`}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  tab === t.key
                    ? 'border-[#8B3FC5] text-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {t.key === 'snapshots' ? `Snapshots (${data.snapshots.length})` : t.label}
              </button>
            ))}
          </div>

          {tab === 'snapshots' && (
            <SnapshotsTab
              data={data}
              form={form} setForm={setForm}
              saving={saving} saveSnapshot={saveSnapshot}
              isAdmin={isAdmin}
              importCSV={importCSV}
              deleteSnapshot={deleteSnapshot}
            />
          )}

          {tab === 'forecast' && (
            <ForecastTab entity={data.entity} runway={data.runway} forecast={forecast} />
          )}

          {tab === 'thresholds' && isAdmin && (
            <ThresholdsTab
              thresholds={thresholds} setThresholds={setThresholds}
              saveThresholds={saveThresholds}
            />
          )}

          {/* Danger zone */}
          {isAdmin && tab === 'snapshots' && (
            <div className="mt-8 pt-5 border-t border-slate-100">
              <button
                onClick={deleteEntity}
                data-testid="finance-entity-delete"
                className="text-xs text-rose-600 hover:text-rose-700 flex items-center gap-1"
              >
                <Trash2 className="w-3 h-3" /> Delete entity (and all snapshots)
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  SUB-TABS
// ─────────────────────────────────────────────────────────────────────────
function SnapshotsTab({ data, form, setForm, saving, saveSnapshot, isAdmin, importCSV, deleteSnapshot }) {
  return (
    <div>
      {isAdmin && (
        <div className="bg-slate-50 rounded-xl p-4 mb-4" data-testid="finance-snapshot-form">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">Record monthly snapshot</div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <input type="text" placeholder="Period YYYY-MM" value={form.period}
              data-testid="finance-form-period"
              onChange={(e) => setForm({ ...form, period: e.target.value })}
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#8B3FC5]" />
            <input type="number" placeholder={`Cash position (${data.entity.currency})`} value={form.cash_position}
              data-testid="finance-form-cash"
              onChange={(e) => setForm({ ...form, cash_position: e.target.value })}
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#8B3FC5]" />
            <input type="number" placeholder="Monthly revenue" value={form.monthly_revenue}
              data-testid="finance-form-revenue"
              onChange={(e) => setForm({ ...form, monthly_revenue: e.target.value })}
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#8B3FC5]" />
            <input type="number" placeholder="Monthly expenses" value={form.monthly_expenses}
              data-testid="finance-form-expenses"
              onChange={(e) => setForm({ ...form, monthly_expenses: e.target.value })}
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#8B3FC5]" />
            <input type="number" placeholder="Headcount (optional)" value={form.headcount}
              data-testid="finance-form-headcount"
              onChange={(e) => setForm({ ...form, headcount: e.target.value })}
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#8B3FC5]" />
            <input type="number" placeholder="New funding (optional)" value={form.new_funding}
              data-testid="finance-form-funding"
              onChange={(e) => setForm({ ...form, new_funding: e.target.value })}
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#8B3FC5]" />
          </div>
          <div className="flex items-center justify-between gap-2">
            <label className="text-xs text-slate-500 cursor-pointer flex items-center gap-1 hover:text-slate-700">
              <Upload className="w-3 h-3" />
              <span data-testid="finance-csv-label">Import CSV</span>
              <input type="file" accept=".csv" className="hidden"
                data-testid="finance-csv-input"
                onChange={(e) => importCSV(e.target.files?.[0])} />
            </label>
            <button
              onClick={saveSnapshot}
              disabled={saving}
              data-testid="finance-snapshot-save"
              className="px-4 py-2 bg-[#0F1A2D] text-white rounded-lg text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? 'Saving…' : <><Plus className="w-4 h-4" /> Record</>}
            </button>
          </div>
          <div className="text-[10px] text-slate-400 mt-2">
            CSV format: period, cash_position, monthly_revenue, monthly_expenses (+ optional monthly_burn, headcount, new_funding, notes)
          </div>
        </div>
      )}

      {data.snapshots.length === 0 ? (
        <div className="text-sm text-slate-500 text-center py-8 border border-dashed border-slate-200 rounded-xl">
          No snapshots yet. Record a month above to start tracking.
        </div>
      ) : (
        <>
          <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4" data-testid="finance-cash-sparkline">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider text-slate-500">Cash trend</div>
              <div className="text-[10px] text-slate-400">{data.snapshots.length} snapshots</div>
            </div>
            <Sparkline
              data={data.snapshots.map((s) => ({ label: s.period, value: s.cash_position }))}
              height={64}
              accent="#8B3FC5"
              formatValue={(v) => fmtCurrency(v, data.entity.currency)}
              showAxis={true}
              testId="finance-sparkline-cash"
            />
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Revenue</div>
              <Sparkline
                data={data.snapshots.map((s) => ({ label: s.period, value: s.monthly_revenue || 0 }))}
                height={48}
                accent="#10B981"
                formatValue={(v) => fmtCurrency(v, data.entity.currency)}
                testId="finance-sparkline-revenue"
              />
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Burn</div>
              <Sparkline
                data={data.snapshots.map((s) => {
                  const b = s.monthly_burn != null ? s.monthly_burn : (s.monthly_expenses - s.monthly_revenue);
                  return { label: s.period, value: Math.max(0, b) };
                })}
                height={48}
                accent="#EF4444"
                formatValue={(v) => fmtCurrency(v, data.entity.currency)}
                testId="finance-sparkline-burn"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="finance-snapshots-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-2">Period</th>
                  <th className="py-2 pr-2 text-right">Cash</th>
                  <th className="py-2 pr-2 text-right">Revenue</th>
                  <th className="py-2 pr-2 text-right">Expenses</th>
                  <th className="py-2 pr-2 text-right">Burn</th>
                  <th className="py-2 pl-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {[...data.snapshots].reverse().map((s) => {
                  const burn = s.monthly_burn != null ? s.monthly_burn : (s.monthly_expenses - s.monthly_revenue);
                  return (
                    <tr key={s.snapshot_id} className="border-b border-slate-100" data-testid={`finance-snapshot-row-${s.period}`}>
                      <td className="py-2 pr-2 font-medium text-slate-900">{s.period}</td>
                      <td className="py-2 pr-2 text-right tabular-nums">{fmtCurrency(s.cash_position, data.entity.currency)}</td>
                      <td className="py-2 pr-2 text-right tabular-nums text-emerald-700">{fmtCurrency(s.monthly_revenue, data.entity.currency)}</td>
                      <td className="py-2 pr-2 text-right tabular-nums text-rose-700">{fmtCurrency(s.monthly_expenses, data.entity.currency)}</td>
                      <td className={`py-2 pr-2 text-right tabular-nums font-medium ${burn > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                        {fmtCurrency(burn, data.entity.currency)}
                      </td>
                      <td className="py-2 pl-2 text-right">
                        {isAdmin && (
                          <button onClick={() => deleteSnapshot(s.snapshot_id)} className="text-slate-400 hover:text-rose-600"
                            data-testid={`finance-snapshot-delete-${s.period}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function ForecastTab({ entity, runway, forecast }) {
  // Find the month the cash goes negative — surface as a "danger date".
  const dangerIdx = forecast.findIndex((p) => p.projected_cash < 0);
  const dangerPoint = dangerIdx >= 0 ? forecast[dangerIdx] : null;
  return (
    <div data-testid="finance-forecast-tab">
      <div className="text-xs text-slate-500 mb-4">
        Straight-line projection of cash position over the next 12 months
        using the entity&rsquo;s 3-month trailing burn rate
        ({fmtCurrency(runway.avg_monthly_burn_3mo, entity.currency)}/mo).
        Real-world burn rarely stays flat — treat this as a worst-case visualisation
        and re-record a snapshot each month to keep it honest.
      </div>

      {forecast.length === 0 ? (
        <div className="bg-slate-50 border border-dashed border-slate-200 rounded-xl p-6 text-center text-sm text-slate-500" data-testid="finance-forecast-empty">
          Add at least one snapshot with a non-zero burn to see a forecast.
        </div>
      ) : (
        <>
          {dangerPoint && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 mb-4 text-sm text-rose-800" data-testid="finance-forecast-danger">
              <strong>Projected cash exhaustion at month +{dangerPoint.month_offset}.</strong>
              {' '}
              Without new revenue, funding, or cost cuts, the entity will run out of cash in
              ~{dangerPoint.month_offset} months. Either raise funding before then or curb
              burn by at least {Math.round(100 / Math.max(1, 12 - dangerPoint.month_offset))}%
              to stretch beyond a year.
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1">
                <BarChart3 className="w-3 h-3" /> 12-month cash projection
              </div>
              <div className="text-[10px] text-slate-400">Forecast (straight-line)</div>
            </div>
            <Sparkline
              data={forecast.map((p) => ({ label: p.label, value: p.projected_cash }))}
              height={120}
              accent={dangerPoint ? '#EF4444' : '#8B3FC5'}
              formatValue={(v) => fmtCurrency(v, entity.currency)}
              showAxis={true}
              testId="finance-sparkline-forecast"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="finance-forecast-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-2">Month</th>
                  <th className="py-2 pr-2 text-right">Projected cash</th>
                  <th className="py-2 pl-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {forecast.map((p) => {
                  const sm = STATUS_META[p.status] || STATUS_META.unknown;
                  return (
                    <tr key={p.month_offset} className="border-b border-slate-100">
                      <td className="py-2 pr-2 font-medium text-slate-900">{p.label}</td>
                      <td className={`py-2 pr-2 text-right tabular-nums ${p.projected_cash < 0 ? 'text-rose-700 font-semibold' : 'text-slate-700'}`}>
                        {fmtCurrency(p.projected_cash, entity.currency)}
                      </td>
                      <td className="py-2 pl-2">
                        <span
                          className="text-[10px] uppercase font-semibold tracking-wider px-1.5 py-0.5 rounded-full"
                          style={{ backgroundColor: sm.bg, color: sm.text }}
                        >
                          {sm.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function ThresholdsTab({ thresholds, setThresholds, saveThresholds }) {
  return (
    <div data-testid="finance-thresholds-form">
      <div className="text-xs text-slate-500 mb-4">
        Custom warning thresholds for this entity. Leave the panel without saving to keep platform defaults
        (12 mo critical / 18 mo warning / 25% burn acceleration / 20% revenue decline / 60 day stale).
      </div>
      <div className="space-y-3">
        {[
          { key: 'critical_runway_months', label: 'Runway critical (months)', def: 12 },
          { key: 'warning_runway_months', label: 'Runway warning (months)', def: 18 },
          { key: 'burn_acceleration_pct', label: 'Burn acceleration (% over 6mo avg)', def: 25 },
          { key: 'revenue_decline_pct', label: 'Revenue decline (% over prior 3mo)', def: 20 },
          { key: 'stale_snapshot_days', label: 'Stale snapshot (days)', def: 60 },
        ].map((row) => (
          <div key={row.key} className="flex items-center justify-between gap-3 py-1">
            <label className="text-sm text-slate-700 flex-1">{row.label}</label>
            <input
              type="number"
              value={thresholds?.[row.key] ?? ''}
              placeholder={`Default ${row.def}`}
              data-testid={`finance-threshold-${row.key}`}
              onChange={(e) => setThresholds({
                ...(thresholds || { critical_runway_months: 12, warning_runway_months: 18, burn_acceleration_pct: 25, revenue_decline_pct: 20, stale_snapshot_days: 60 }),
                [row.key]: e.target.value === '' ? row.def : parseFloat(e.target.value),
              })}
              className="w-32 px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#8B3FC5] tabular-nums"
            />
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={() => { setThresholds(null); saveThresholds(); }} className="text-xs text-slate-500 hover:text-slate-900">
          Reset to defaults
        </button>
        <button
          onClick={saveThresholds}
          data-testid="finance-thresholds-save"
          className="px-4 py-2 bg-[#0F1A2D] text-white rounded-lg text-sm font-semibold hover:bg-slate-800 flex items-center gap-2"
        >
          <Save className="w-4 h-4" /> Save thresholds
        </button>
      </div>
    </div>
  );
}
